import React, { useMemo } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official/dist/highcharts-react";
import classes from './RightSidebar.module.css';
import { useDispatch, useSelector } from 'react-redux';
import { selectionsSliceActions } from "../store/slices/selectionsSlice";
import IDPMovement from "./IDPMovement";
import RoundReason from "./RoundReason";

const RightSidebar = () => {
    const dispatch = useDispatch();
    const selectedCountry = useSelector(state => state.selection.country)
    const selectedAdmin1 = useSelector(state => state.selection.admin1)
    const selectedAdmin2 = useSelector(state => state.selection.admin2)
   
    

    const idps = useSelector(state => state.selection.idps)
    const maleidps = useSelector(state => state.selection.maleidps)
    const femaleidps = useSelector(state => state.selection.femaleidps)
    const region = useSelector(state => state.selection.region)
    const selectedAssessmentDate = useSelector(state => state.selection.assessmentDate)
    const overallIdpsTrend = useSelector(state => state.selection.overallIdpsTrend)

    const formatNumber = (value) => {
        const numericValue = Number(value);
        if (!Number.isFinite(numericValue)) return value ?? "";
        return new Intl.NumberFormat("en-US").format(numericValue);
    };

    const isZero = (value) => {
        const numericValue = Number(String(value ?? "").replace(/,/g, "").trim());
        return !Number.isFinite(numericValue) || numericValue === 0;
    };


    const formatDate = (value) => {
        if (!value) return "";
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return String(value);
        return new Intl.DateTimeFormat("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric",
            timeZone: "UTC",
        }).format(date);
    };

    const missingValue = "\u2014";
    const assessmentDateLabel = formatDate(selectedAssessmentDate);
    const hierarchyItems = [
        selectedAdmin1 ? { name: selectedAdmin1, label: "ADMIN 1" } : null,
        selectedAdmin2 ? { name: selectedAdmin2, label: "ADMIN 2" } : null,
    ].filter(Boolean);

    const closeSidebar = () => {
        // Clear the full drill-down (country + admins + region) so the map
        // returns to the global regional overview, not the region's countries.
        // Clearing the region cascades to reset the active region on the map.
        dispatch(selectionsSliceActions.setCountry(""));
        dispatch(selectionsSliceActions.setCountryPcode(""));
        dispatch(selectionsSliceActions.setAdmin1(""));
        dispatch(selectionsSliceActions.setAdmin1Pcode(""));
        dispatch(selectionsSliceActions.setAdmin2(""));
        dispatch(selectionsSliceActions.setRegion(""));
        dispatch(selectionsSliceActions.setAssessmentDate(""));
        dispatch(selectionsSliceActions.setRoundFilter(null));
        dispatch(selectionsSliceActions.setPopulationType("total"));
        dispatch(selectionsSliceActions.setYear(2026));
    }

    const { observedCategories, observedSeries } = useMemo(() => {
        const totalsByYear = new Map();
        (overallIdpsTrend || []).forEach((row) => {
            const year = Number(row.year);
            if (!Number.isFinite(year)) return;
            const idpsValue = Number(row.idps);
            const current = totalsByYear.get(year) ?? 0;
            totalsByYear.set(year, current + (Number.isFinite(idpsValue) ? idpsValue : 0));
        });

        const years = Array.from(totalsByYear.keys()).sort((a, b) => a - b);
        return {
            observedCategories: years.map((year) => String(year)),
            observedSeries: years.map((year) => {
                const rawValue = totalsByYear.get(year) ?? 0;
                return {
                    y: rawValue / 1_000_000,
                    raw: rawValue,
                };
            }),
        };
    }, [overallIdpsTrend]);
    const observedOptions = {
        chart: {
            type: "line",
            height: 200,
            backgroundColor: "transparent",
            spacing: [10, 10, 0, 0],
        },
        title: { text: null },
        credits: { enabled: false },
        legend: { enabled: false },
        xAxis: {
            categories: observedCategories,
            tickLength: 0,
            lineColor: "#b9c6e3",
            labels: {
                style: { color: "#3b3b3b", fontSize: "11px" },
                rotation: 0,
                allowOverlap: false,
                step: 4,
            },
        },
        yAxis: {
            title: { text: null },
            min: 0,
            gridLineWidth: 0,
            labels: {
                style: { color: "#3b3b3b", fontSize: "11px" },
                formatter: function () {
                    return `${this.value}M`;
                },
            },
        },
        tooltip: {
            enabled: true,
            useHTML: true,
            formatter: function () {
                const rawValue = Number(this.point?.raw);
                const formattedValue = new Intl.NumberFormat("en-US").format(
                    Number.isFinite(rawValue) ? rawValue : 0,
                );
                const yearLabel = this.point?.category ?? this.key ?? this.x ?? "";
                return `
                    <div style="padding: 4px 6px;">
                        <div style="font-weight: 600; margin-bottom: 4px;">${yearLabel}</div>
                        <div style="display: flex; align-items: center; gap: 6px;">
                            <span style="width: 8px; height: 8px; border-radius: 999px; background: #4f6ddf; display: inline-block;"></span>
                            <span>${formattedValue}</span>
                        </div>
                    </div>
                `;
            },
        },
        plotOptions: {
            series: {
                marker: { enabled: true, radius: 3 },
                lineWidth: 3,
                color: "#0033A0",
            },
        },
        series: [
            {
                data: observedSeries,
            },
        ],
    };
    // IDP Proportion section commented out.
    // eslint-disable-next-line no-unused-vars
    const donutGlobalData = [
        { value: 60, amount: 60, color: "#0033A0" },
        { value: 40, amount: 40, color: "#C9D5EE" },
    ];
    // eslint-disable-next-line no-unused-vars
    const donutRegionalData = [
        { value: 65, amount: 65, color: "#0033A0" },
        { value: 35, amount: 35, color: "#C9D5EE" },
    ];
    // eslint-disable-next-line no-unused-vars
    const donutGlobalOptions = {
        chart: {
            type: "pie",
            height: 130,
            backgroundColor: "transparent",
        },
        title: { text: null },
        credits: { enabled: false },
        legend: { enabled: false },
        tooltip: { enabled: false },
        plotOptions: {
            pie: {
                innerSize: "65%",
                dataLabels: {
                    enabled: true,
                    distance: 8,
                    inside: false,
                    crop: false,
                    overflow: "allow",
                    allowOverlap: true,
                    connectorWidth: 1,
                    connectorColor: "#9AA7C8",
                    style: {
                        color: "#8a8a8a",
                        fontWeight: "bold",
                        textOutline: "none",
                        fontSize: "11px",
                    },
                    formatter: function () {
                        return `${this.point.percentage.toFixed(0)}%<br/>${this.y}M`;
                    },
                },
                borderWidth: 0,
                startAngle: 30,
                size: "85%",
            },
        },
        series: [
            {
                data: donutGlobalData.map((item) => ({ y: item.value, color: item.color })),
            },
        ],
    };
    // eslint-disable-next-line no-unused-vars
    const donutRegionalOptions = {
        chart: {
            type: "pie",
            height: 130,
            backgroundColor: "transparent",
        },
        title: { text: null },
        credits: { enabled: false },
        legend: { enabled: false },
        tooltip: { enabled: false },
        plotOptions: {
            pie: {
                innerSize: "65%",
                dataLabels: {
                    enabled: true,
                    distance: 8,
                    inside: false,
                    crop: false,
                    overflow: "allow",
                    allowOverlap: true,
                    connectorWidth: 1,
                    connectorColor: "#9AA7C8",
                    style: {
                        color: "#8a8a8a",
                        fontWeight: "bold",
                        textOutline: "none",
                        fontSize: "11px",
                    },
                    formatter: function () {
                        return `${this.point.percentage.toFixed(0)}%<br/>${this.y}M`;
                    },
                },
                borderWidth: 0,
                startAngle: 30,
                size: "85%",
            },
        },
        series: [
            {
                data: donutRegionalData.map((item) => ({ y: item.value, color: item.color })),
            },
        ],
    };

    return (
        <>
            <div className={classes.box}>
                <div className={classes.summary_box}>
                    <div className={classes.summary_box_header}>
                        <button type="button" onClick={closeSidebar} className={classes.closesidebar} aria-label="Close sidebar">
                            <span className={classes.close_icon} aria-hidden="true" />
                        </button>

                        <div className={classes.summary_heading}>
                            <p className={classes.summary_box_title}>{selectedCountry}</p>
                            {region && <p className={classes.summary_box_region}>{region}</p>}
                        </div>

                        {hierarchyItems.length > 0 && (
                            <div className={classes.location_hierarchy} aria-label="Selected admin hierarchy">
                                {hierarchyItems.map((item, index) => (
                                    <React.Fragment key={`${item.label}-${item.name}`}>
                                        {index > 0 && (
                                            <span className={classes.hierarchy_arrow} aria-hidden="true">
                                                {"\u2192"}
                                            </span>
                                        )}
                                        <span className={classes.hierarchy_item}>
                                            <span className={classes.hierarchy_name}>{item.name}</span>
                                            <span className={classes.hierarchy_label}>{item.label}</span>
                                        </span>
                                    </React.Fragment>
                                ))}
                            </div>
                        )}

                        <div className={classes.summary_box_assessment}>
                            <span>Latest Assessment</span>
                            <span className={classes.assessment_info_icon} aria-label="Latest assessment date">i</span>
                        </div>
                    </div>

                    <div className={classes.summary_box_body}>
                        <p className={classes.assessment_date}>{assessmentDateLabel || ""}</p>

                        <div className={classes.stat_grid}>
                            <div className={`${classes.stat} ${classes.stat_total}${isZero(idps) ? ` ${classes.stat_empty}` : ""}`}>
                                <span className={classes.stat_icon_badge}>
                                    <span className={classes.stat_icon} style={{ WebkitMaskImage: "url(/images/idps.svg)", maskImage: "url(/images/idps.svg)" }} />
                                </span>
                                <div className={classes.stat_text}>
                                    <p className={classes.aggregated_figures_figure}>{isZero(idps) ? missingValue : formatNumber(idps)}</p>
                                    <p className={classes.aggregated_figures_figure_text}>IDPs</p>
                                </div>
                            </div>

                            <div className={`${classes.stat}${isZero(maleidps) ? ` ${classes.stat_empty}` : ""}`}>
                                <span className={classes.stat_icon_badge}>
                                    <span className={classes.stat_icon} style={{ WebkitMaskImage: "url(/images/male_idps.svg)", maskImage: "url(/images/male_idps.svg)" }} />
                                </span>
                                <div className={classes.stat_text}>
                                    <p className={classes.aggregated_figures_figure}>{isZero(maleidps) ? missingValue : formatNumber(maleidps)}</p>
                                    <p className={classes.aggregated_figures_figure_text}>Male IDPs</p>
                                </div>
                            </div>

                            <div className={`${classes.stat}${isZero(femaleidps) ? ` ${classes.stat_empty}` : ""}`}>
                                <span className={classes.stat_icon_badge}>
                                    <span className={classes.stat_icon} style={{ WebkitMaskImage: "url(/images/female_idps.svg)", maskImage: "url(/images/female_idps.svg)" }} />
                                </span>
                                <div className={classes.stat_text}>
                                    <p className={classes.aggregated_figures_figure}>{isZero(femaleidps) ? missingValue : formatNumber(femaleidps)}</p>
                                    <p className={classes.aggregated_figures_figure_text}>Female IDPs</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <IDPMovement />

            <RoundReason />

            <div className={classes.box_other}>
                <div className={classes.idps_observed_box}>
                    <p className={classes.idps_box_title}>Overall IDPs Observed</p>
                    <div className={classes.idps_section_separator} />

                    <div className={classes.idps_observed_chart}>
                        <HighchartsReact highcharts={Highcharts} options={observedOptions} />
                    </div>
                </div>
            </div>

            {/* <div className={classes.box_other}>
                <div className={classes.idps_proportion_box}>
                    <p className={classes.idps_box_title}>IDP Proportion</p>
                    <div className={classes.idps_section_separator} />
                    <div className={classes.idps_donut_row}>
                        <div className={classes.idps_donut_card}>
                            <p className={classes.idps_donut_title}>Global</p>
                            <div className={classes.idps_donut_body}>
                                <div className={classes.idps_donut_chart}>
                                    <HighchartsReact highcharts={Highcharts} options={donutGlobalOptions} />
                                </div>
                            </div>
                        </div>
                        <div className={classes.idps_donut_card}>
                            <p className={classes.idps_donut_title}>Regional</p>
                            <div className={classes.idps_donut_body}>
                                <div className={classes.idps_donut_chart}>
                                    <HighchartsReact highcharts={Highcharts} options={donutRegionalOptions} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div> */}




        </>
    )
}

export default RightSidebar
